import { Form, Modal, Select } from "antd";
import type { HTMLAttributes } from "react";
import { useIntl } from "react-intl";
import { FormCheckbox } from "~components/Form/FormCheckbox";
import { FormSection, type FormSectionLabel } from "~components/Form/FormSection";
import { FormSelect } from "~components/Form/FormSelect";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { CompetitorTypes } from "~enums/competitorTypes";
import { FixtureResultStatuses } from "~enums/fixtureResultStatuses";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { useGetFixture } from "~pages/Fixtures/hooks";
import type {
  EditFixtureForm,
  EditFixtureFormKeys,
  FixtureEntityTransformed,
  FixtureEntrantTransformed,
  FixturePersonTransformed,
} from "~pages/Fixtures/types";
import { getIsScoreEqual } from "../../utilities";
import { FixtureScore } from "../FixtureScore";
import { StyledFlexWrapper, StyledFormItemContainer } from "./FixtureBasicSection.styles";

type Props = HTMLAttributes<HTMLDivElement> & {
  isUpdating: boolean;
};

const { confirm } = Modal;

export const FixtureBasicSection = ({ isUpdating, ...props }: Props) => {
  const { formatMessage } = useIntl();
  const form = Form.useFormInstance<EditFixtureForm>();
  const { data: fixtureData, isLoading, isFetching } = useGetFixture();

  const competitors = fixtureData?.formData.competitors;
  const participants = fixtureData?.formData.participants;
  const entrants = competitors?.length ? competitors : participants;

  type Entrants = typeof entrants;
  const homeEntrants = entrants?.filter((x) => x.isHome) as Entrants;
  const awayEntrants = entrants?.filter((x) => !x.isHome) as Entrants;

  const isCompetitors = fixtureData?.competitorType === CompetitorTypes.Entity;
  const isParticipants = fixtureData?.competitorType === CompetitorTypes.Person;

  const mapFn = (entrant: FixtureEntityTransformed | FixturePersonTransformed) => entrant.nameAbbreviation || entrant.name;
  const homeLabel = homeEntrants?.map(mapFn).join(" / ") || "";
  const awayLabel = awayEntrants?.map(mapFn).join(" / ") || "";

  const { fixtureStatuses } = useSportConfiguration();

  // Secondary Score disabled logic (enable, if scores are equal)
  const homeScoreKey: EditFixtureFormKeys = "homeScore";
  const awayScoreKey: EditFixtureFormKeys = "awayScore";
  const homeScore = Form.useWatch<string>([homeScoreKey], form);
  const awayScore = Form.useWatch<string>([awayScoreKey], form);
  const secondaryScoreEnabled = getIsScoreEqual(homeScore, awayScore);

  const neutralVenueKey = isParticipants ? "isNeutralVenue.participants" : "isNeutralVenue.competitors";

  const fields: FormSectionLabel[] = [
    { titleIntlKey: "score", descriptionIntlKey: "form.help.fixture.score" },
    {
      titleIntlKey: "secondaryScore",
      descriptionIntlKey: "form.help.fixture.secondary.score",
    },
    {
      titleIntlKey: "resultStatus",
      descriptionIntlKey: {
        key: "form.help.status.item",
        item: "match",
      },
    },
    {
      titleIntlKey: neutralVenueKey,
      descriptionIntlKey: "form.help.fixture.neutral.venue",
    },
    ...(isCompetitors
      ? [
          {
            titleIntlKey: "includeInConferenceStatistics",
            descriptionIntlKey: "form.help.fixture.include.statistics",
          },
        ]
      : []),
  ];

  const isFixtureOnly = (!isCompetitors && !isParticipants) || !entrants?.length;

  const { hasPermission } = useUserPermissions();
  const readFixtureAllowed = hasPermission(PermissionNames.FIXTURE, PermissionOperations.READ);
  const readParticipantsAllowed = hasPermission(PermissionNames.FIXTURE_PERSON, PermissionOperations.READ);
  const readCompetitorsAllowed = hasPermission(PermissionNames.FIXTURE_ENTITY, PermissionOperations.READ);

  const entrantReadAllowed = isCompetitors ? readCompetitorsAllowed : isParticipants ? readParticipantsAllowed : false;

  return (
    <div {...props}>
      <FormSection title={formatMessage({ id: "fixture.information" })} fields={fields} />
      <StyledFlexWrapper $marginBottom={6}>
        {(isLoading || (!isFixtureOnly && entrantReadAllowed)) && (
          <>
            <FixtureScore<EditFixtureForm>
              awayProps={awayEntrants?.length ? { label: awayLabel, name: "awayScore" } : null}
              homeProps={homeEntrants?.length ? { label: homeLabel, name: "homeScore" } : null}
              label={formatMessage({ id: "score" })}
              showSkeleton={isLoading}
              disabled={isFetching || isUpdating}
            />
            <FixtureScore<EditFixtureForm>
              awayProps={awayEntrants?.length ? { label: awayLabel, name: "awaySecondaryScore" } : null}
              homeProps={homeEntrants?.length ? { label: homeLabel, name: "homeSecondaryScore" } : null}
              disabled={!secondaryScoreEnabled || isFetching || isUpdating}
              label={formatMessage({ id: "secondaryScore" })}
              showSkeleton={isLoading}
            />
          </>
        )}
        {readFixtureAllowed && (
          <StyledFormItemContainer>
            <FormSelect<EditFixtureForm>
              label={formatMessage({ id: "resultStatus" })}
              name="status"
              showSkeleton={isLoading}
              disabled={isFetching || isUpdating}
              selectProps={{
                onChange: isFixtureOnly
                  ? undefined
                  : (value) => {
                      const entrantKey: EditFixtureFormKeys = isCompetitors ? "competitors" : "participants";
                      const entrants: FixtureEntrantTransformed[] = form.getFieldValue(entrantKey);
                      const entrantUniqueStatuses = [...new Set(entrants.map((x) => x.resultStatus))];
                      const isStatusMatch = Object.values(FixtureResultStatuses).includes(value);

                      // if all entrants already have the same status as fixture status, no need to replace
                      if (entrantUniqueStatuses.length === 1 && entrantUniqueStatuses[0] === value) return;

                      if (isStatusMatch) {
                        const titleIntlKey = isCompetitors
                          ? "fixture.status.confirm.title.competitors"
                          : "fixture.status.confirm.title.participants";

                        const confirmIntlKey = isCompetitors
                          ? "fixture.status.confirm.confirm.competitors"
                          : "fixture.status.confirm.confirm.participants";

                        confirm({
                          title: formatMessage({ id: titleIntlKey }),
                          content: formatMessage({ id: confirmIntlKey }, { status: value }),
                          okText: formatMessage({ id: "YES" }),
                          cancelText: formatMessage({ id: "NO" }),
                          icon: null,
                          onOk: () => {
                            entrants.forEach((_, idx) => {
                              form.setFieldValue([entrantKey, idx, "resultStatus" satisfies keyof FixtureEntrantTransformed], value);
                            });
                          },
                        });
                      }
                    },
              }}
            >
              {fixtureStatuses.map((status) => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </FormSelect>
          </StyledFormItemContainer>
        )}
      </StyledFlexWrapper>
      <StyledFlexWrapper>
        {(isLoading || (!isFixtureOnly && entrantReadAllowed)) && (
          <FormCheckbox<EditFixtureForm>
            label={formatMessage({ id: neutralVenueKey })}
            name="isNeutralVenue"
            disabled={isFetching || isUpdating}
          />
        )}
        {!isFixtureOnly && isCompetitors && readCompetitorsAllowed && (
          <FormCheckbox<EditFixtureForm>
            label={formatMessage({ id: "includeInConferenceStatistics" })}
            name="includeInConferenceStatistics"
            disabled={isFetching || isUpdating}
          />
        )}
      </StyledFlexWrapper>
    </div>
  );
};
